import { tt } from "../i18n/tt";
import { TERMS_DOCS } from "../content/partnerTerms";
import { lazyI18n } from "../i18n/lazyText";
// 파트너센터 — 순수 함수·상수 (IO 없음). 기획 docs/ops/partner-program-plan.md §4·§5·§6 · 두 유형 docs/ops/partner-two-types-spec.md
// 화면은 src/pages/PartnerHub.jsx · /partner/leads 는 src/pages/PartnerLeads.jsx, 약관 모달은 src/components/partner/PartnerTermsModal.jsx.
//
// 🔒 수익 쉐어 비율표(SHARE_ROWS)는 **안내용**이다. 실제 지급 계산은 여기서 하지 않는다 —
//    정산은 현행 RPC partner_payout_rate(partner_program.payout_rate·rate_schedule) · get_partner_payout_summary 가 그대로 담당한다(기획 §4-3 은 다음 단계).
// 🔒 약관 문안(TERMS_DOCS, src/content/partnerTerms.js)은 해시 대상이라 tt() 로 감싸지 않는다.

export { TERMS_DOCS };
export const TERMS_KEYS = TERMS_DOCS.map((d) => d.key);
/** 종전 표준 계약(2026-09-09 ~ 09-11) 키 — 이력 표시용. 서명 판정에는 세지 않는다 */
export const LEGACY_CONTRACT_KEY = "partner_contract";

// 유형 이름 (대표 표기 2026-09-11): video=영상권리사 파트너 · marketing=크리에이터 파트너 · both=둘 다
export const PARTNER_TYPE_LABEL = lazyI18n(() => ({
  video: tt("a.5a19b661", "영상권리사 파트너"),
  marketing: tt("a.b1a315ab", "크리에이터 파트너"),
  both: tt("a.d10accb2", "영상권리사 · 크리에이터 파트너"),
}));
export const PARTNER_TYPE_SHORT = lazyI18n(() => ({
  video: tt("a.447f0fcb", "영상권리사"),
  marketing: tt("a.567c11be", "크리에이터"),
  both: tt("a.35c75c48", "둘 다"),
}));
export const PARTNER_TYPES = ["video", "marketing", "both"];

/** 화면이 보는 유형 — 담당자 확인 뒤엔 partner_type, 확인 전엔 본인 선언값. 선언 전이면 null */
export function partnerTypeOf(program) {
  if (!program) return null;
  return program.type_confirmed ? program.partner_type || null : program.declared_type || null;
}

/** 레일·머리에 붙는 유형 배지 — 확정(보라) · 선언만(회색, 「(확인 전)」 꼬리) · 선언 전(회색) */
export function partnerTypeBadge(program) {
  if (program && program.type_confirmed && PARTNER_TYPE_LABEL[program.partner_type]) {
    return { label: PARTNER_TYPE_LABEL[program.partner_type], scheme: "purple", confirmed: true };
  }
  if (program && program.declared_type && PARTNER_TYPE_LABEL[program.declared_type]) {
    return { label: tt("a.f6a18d39", "{{v0}} (확인 전)", { v0: PARTNER_TYPE_LABEL[program.declared_type] }), scheme: "gray", confirmed: false };
  }
  return { label: tt("a.a526dd31", "유형 선언 전"), scheme: "gray", confirmed: false };
}

/** 이 파트너에게 그 활동(video=권리사 영업 · marketing=크리에이터 모집)이 열려 있나 */
export function typesInclude(program, t) {
  const k = partnerTypeOf(program);
  return k === "both" || k === t;
}

/** 유형별 필수 약관 문서 키 (🔒 서버 partner_terms_required_keys 와 같은 규칙) */
export function termsRequiredFor(type) {
  if (!type) return [];
  return TERMS_DOCS.filter((d) => d.appliesTo.includes(type)).map((d) => d.key);
}

export function getTermsDoc(key) {
  return TERMS_DOCS.find((d) => d.key === key) || null;
}

/**
 * 그림 확인(figureCheck, 2026-09-19 공통 약관 v1.5)의 글자 대체문 — 화면(PartnerShareMatrix)이 그리는 글자와 **같은 데이터**에서 만든다.
 * 그림의 글자가 서명 해시에 묶여, 그림만 바꾸고 버전을 안 올리는 실수를 막는다.
 */
export function figureCheckText(fig) {
  if (!fig) return [];
  return [
    `[그림 ${fig.key} v${fig.version}] ${fig.title}`,
    ...fig.cells.map((c) => `(${fig.rowLabels[c.row]} × ${fig.colLabels[c.col]}) ${c.title} — ${c.desc}`),
    ...fig.notes,
    `[별도 확인] ${fig.label}`,
  ];
}

/** 해시 대상 문안 전문 — 제목·버전·키 + 조문 + 체크 문구 + 별도 확인 문구 + 그림 확인 글자. 어디서 만들어도 같은 문자열이어야 한다. */
export function termsDocumentText(doc) {
  if (!doc) return "";
  return [
    `${doc.title} ${doc.version} (${doc.key})`,
    ...(doc.intro ? [doc.intro] : []),
    ...doc.sections.map(([h, b]) => (b ? `${h}\n${b}` : h)),
    ...doc.checks.map((c) => `[ ] ${c.label}`),
    ...(doc.separateChecks || []).map((c) => tt("a.7991cb04", "[별도 확인] {{v0}}", { v0: c.label })),
    ...figureCheckText(doc.figureCheck),
  ].join("\n\n");
}

/** 서버 요구 버전(terms_versions_required)이 번들 문안 버전과 다른 문서가 하나라도 있으면 서명을 잠근다 (「갱신 배포 중」) */
export function termsStale(program, keys) {
  if (!program) return false;
  const req = program.terms_versions_required || {};
  const list = keys || termsRequiredFor(partnerTypeOf(program));
  return list.some((k) => {
    const d = getTermsDoc(k);
    return !d || req[k] !== d.version;
  });
}

/**
 * 요구 버전으로 서명된 문서 / 남은 문서.
 * agreements: partner_agreement 행들(agreement_key·version·agreed_at·signer_name). 종전 partner_contract 키는 세지 않는다.
 */
export function signedDocs(agreements, program, keys) {
  const req = (program && program.terms_versions_required) || {};
  const list = keys || termsRequiredFor(partnerTypeOf(program));
  const signed = {};
  (agreements || []).forEach((a) => {
    if (!a || !list.includes(a.agreement_key)) return;
    if (a.version !== req[a.agreement_key]) return;
    if (!signed[a.agreement_key]) signed[a.agreement_key] = a;
  });
  return { signed, missing: list.filter((k) => !signed[k]) };
}

export const LINK_SOURCES = lazyI18n(() => [
  ["instagram", tt("a.4f2517b0", "인스타그램")], ["youtube", tt("a.2b53fead", "유튜브")], ["threads", tt("a.cff2fdc6", "스레드")], ["blog", tt("a.319f9f74", "블로그")],
  ["kakao", tt("a.6ab9a8a0", "카카오톡")], ["x", "X"], ["tiktok", tt("a.98460574", "틱톡")], ["naver", tt("a.ed0ad568", "네이버")], ["offline", tt("a.c8305a0a", "오프라인")], ["other", tt("a.7f598db0", "기타")],
]);
export const LINK_MEDIUMS = ["post", "story", "video", "dm", "link_in_bio"];

/** 대가성 표시 표준 문구 — 부속 B B-4조 5항(v1.4)·공통 약관 제8조 8호. 문안을 바꾸면 약관 문안도 같이 바꾼다(벤치마크 R-1, 2026-09-11) */
export const PARTNER_DISCLOSURE_TEXT = "이 게시물은 레이블리 파트너 활동의 일환으로, 소개에 따른 대가를 받습니다."; // 공정위 표시 원문 — 번역하지 않는다 // 법적 동의·표시 원문 — 서버 기록·정확한 문구가 필요해 번역하지 않는다
/** 최소 지급액(원) — 공통 약관 제11조 7항(v1.4). 이 금액 미만인 정산월은 다음 달로 이월. 서버 합산 게이트는 기획 §S-3(2단계) */
export const PARTNER_MIN_PAYOUT_KRW = 10000;

/** 유입 추적 기획(partner-attribution-plan §4-1) 링크 규격. campaign 은 선택. */
export function buildPartnerLink({ code, source = "instagram", medium = "post", campaign = "", base = "https://laeebly.io/signup" } = {}) {
  if (!code) return "";
  const p = new URLSearchParams();
  p.set("ref", String(code).toUpperCase());
  p.set("utm_source", source);
  p.set("utm_medium", medium);
  const c = String(campaign || "").trim();
  if (c) p.set("utm_campaign", c);
  return `${base}?${p.toString()}`;
}

// ── 짧은 링크 (2026-09-28 대표 결정 ④ · 시안 3-A) ─────────────────────────────────────────────────────────────
//   https://laeebly.io/go/PT-73757        → /signup?ref=PT-73757&utm_source=other&utm_medium=shortlink
//   https://laeebly.io/go/PT-73757/ig     → /signup?ref=PT-73757&utm_source=instagram&utm_medium=shortlink
//   https://laeebly.io/go/PT-73757/ig?c=2026-10 → … &utm_campaign=2026-10
//   서버·도메인 작업 없음 — App.jsx 가 /go/… 를 보면 같은 창에서 /signup?… 로 바꿔 연다(전체 페이지 이동). 가입 화면 마운트가
//   기존 captureAndLogVisit 로 유입 보관 + 방문 1건을 기록한다(활성 코드만 · 24시간 중복 제거 — 기존 규칙 그대로).
//   코드가 주소에 그대로 보이므로 부속 B B-3조 7호(코드를 숨기는 단축 URL 은 실적 제외)와 충돌하지 않는다.
export const SHORT_LINK_BASE = "https://laeebly.io/go";
export const SHORT_LINK_MEDIUM = "shortlink";
/** 접미 → utm_source 표준값(attribution.STANDARD_SOURCES 와 같은 값만). qr = 인쇄물·오프라인 */
export const SHORT_SOURCE_CODES = { ig: "instagram", yt: "youtube", th: "threads", bl: "blog", kk: "kakao", x: "x", tt: "tiktok", nv: "naver", qr: "offline" };
const SOURCE_TO_SHORT = Object.fromEntries(Object.entries(SHORT_SOURCE_CODES).map(([k, v]) => [v, k]));

/** utm_source 표준값 → 짧은 링크 접미. offline → qr, other/모름 → null(접미 없음) */
export function shortCodeOfSource(source) {
  return SOURCE_TO_SHORT[String(source || "").toLowerCase()] || null;
}

/** 파트너센터 「짧은 링크」 — source 가 표준값이면 접미를 붙이고, 아니면 코드만. campaign 은 ?c= (선택) */
export function buildShortLink({ code, source = "instagram", campaign = "", base = SHORT_LINK_BASE } = {}) {
  if (!code) return "";
  const c = String(code).toUpperCase();
  const suffix = shortCodeOfSource(source);
  const camp = String(campaign || "").trim();
  return `${base}/${c}${suffix ? `/${suffix}` : ""}${camp ? `?c=${encodeURIComponent(camp)}` : ""}`;
}

/**
 * /go/코드[/접미][?c=캠페인] → 가입 화면 주소. 코드 형식이 틀리면 /signup(코드 없이). 다른 경로면 null.
 * 순수 함수 — App.jsx 와 테스트가 같은 것을 쓴다.
 */
export function shortLinkRedirectTarget(pathname, search = "") {
  const m = String(pathname || "").match(/^\/go(?:\/([^/]*))?(?:\/([^/]*))?\/?$/);
  if (!m) return null;
  const ref = String(m[1] || "").trim().toUpperCase();
  if (!/^[A-Z0-9-]{2,16}$/.test(ref)) return "/signup";
  const src = SHORT_SOURCE_CODES[String(m[2] || "").toLowerCase()] || "other";
  const p = new URLSearchParams();
  p.set("ref", ref);
  p.set("utm_source", src);
  p.set("utm_medium", SHORT_LINK_MEDIUM);
  let camp = "";
  try { camp = String(new URLSearchParams(search || "").get("c") || "").trim(); } catch (e) { camp = ""; }
  if (camp) p.set("utm_campaign", camp.slice(0, 64));
  return `/signup?${p.toString()}`;
}

/** 안내용 비율표 — 내 몫만. 다른 파트너의 몫·존재는 보이지 않는다(기획 §4-2·§8). 유형별 표준 비율 숫자는 어디에도 쓰지 않는다 */
const rowRights = () => [
  [tt("a.882cdf9b", "레이블리가 직접 모집한 크리에이터가 내 권리사의 작품으로 낸 수익"), tt("a.3b60e668", "표준 비율 그대로"), false],
  [tt("a.d1f16c63", "크리에이터 파트너가 모집한 크리에이터가 낸 수익 (함께 만든 수익)"), tt("a.07f6718b", "표준 비율의 절반"), true],
];
const rowCreator = () => [
  [tt("a.40199692", "레이블리가 직접 계약한 작품으로 내가 모집한 크리에이터가 낸 수익"), tt("a.3b60e668", "표준 비율 그대로"), false],
  [tt("a.d7c8423a", "영상권리사 파트너가 가져온 작품으로 낸 수익 (함께 만든 수익)"), tt("a.07f6718b", "표준 비율의 절반"), true],
];
export const SHARE_ROWS = lazyI18n(() => ({
  // 유형 확인 전(type_confirmed=false)에는 유형을 단정하지 않고 규칙만 보여 준다
  generic: [
    [tt("a.9dfc678b", "한 수익에 파트너가 나 혼자일 때"), tt("a.3b60e668", "표준 비율 그대로"), false],
    [tt("a.f65a7f28", "영상권리사 파트너와 크리에이터 파트너가 함께 만든 수익 (혼자였다면 없던 수익)"), tt("a.07f6718b", "표준 비율의 절반"), true],
  ],
  video: rowRights(),
  marketing: rowCreator(),
  both: [
    ...rowRights(),
    ...rowCreator(),
    [tt("a.521e12c5", "내 권리사의 작품으로 내가 모집한 크리에이터가 낸 수익"), tt("a.c1e9425a", "두 몫 모두 표준 비율 그대로 (겹치는 상대가 없음)"), false],
  ],
}));

/**
 * 화면 단계. 서버 status 를 그대로 쓰되, `signed` 인데 이전 버전 이수 기록이 있으면 「재수료 대기」로 구분한다.
 * @returns 'pending' | 'signed' | 'update_due' | 'active' | 'suspended' | 'terminated'
 */
export function stageOf(program, completions = []) {
  if (!program) return "pending";
  const s = program.status;
  if (s === "signed") {
    const hadAny = (completions || []).some((c) => c.lesson_key === "partner_onboarding");
    return hadAny ? "update_due" : "signed";
  }
  return s || "pending";
}

export const STAGE_LABEL = lazyI18n(() => ({
  pending: { label: tt("a.91449a7b", "약관 서명 필요"), scheme: "orange" },
  signed: { label: tt("a.8d0c6ec8", "수료 필요"), scheme: "orange" },
  update_due: { label: tt("a.c3990f4b", "재수료 필요"), scheme: "orange" },
  active: { label: tt("a.d3033d3a", "활성"), scheme: "green" },
  suspended: { label: tt("a.5db669e6", "정지 · 코드 비활성"), scheme: "red" },
  terminated: { label: tt("a.b239ce12", "해지"), scheme: "gray" },
}));

/**
 * 파트너 화면(레일·홈 머리)의 단계 배지 — 서명 전(pending)은 「약관 서명 필요」 대신 **「둘러보기」**(2026-09-11, 기획 partner-center-access-plan §5-3).
 * 어드민 화면은 STAGE_LABEL 을 그대로 쓴다(운영에겐 「계약 전」이 필요한 정보).
 */
export const BROWSE_STAGE = lazyI18n(() => ({ label: tt("a.597f6cbc", "둘러보기"), scheme: "gray" }));
export function partnerStageBadge(status) {
  if (!status || status === "pending") return BROWSE_STAGE;
  return STAGE_LABEL[status] || BROWSE_STAGE;
}

/** 재수료 기한까지 남은 일수 (기한 없으면 null, 지났으면 음수) */
export function daysUntil(deadline, now = new Date()) {
  if (!deadline) return null;
  const d = new Date(deadline);
  if (Number.isNaN(d.getTime())) return null;
  return Math.ceil((d.getTime() - now.getTime()) / 86400000);
}

// ── 권리사 영업 (partner_lead) ──────────────────────────────────────────────

/** partner_lead.status 라벨 — 값은 DB check 와 같다 */
export const LEAD_STATUS_LABEL = lazyI18n(() => ({
  submitted: { label: tt("a.e9d2de78", "등록됨 · 회신 대기"), scheme: "orange" },
  approved: { label: tt("a.51c77e58", "승인"), scheme: "green" },
  rejected: { label: tt("a.0669ba01", "반려"), scheme: "red" },
  meeting: { label: tt("a.475cb533", "미팅"), scheme: "blue" },
  contracted: { label: tt("a.f6d49a89", "계약"), scheme: "purple" },
  dropped: { label: tt("a.36b7d781", "종료"), scheme: "gray" },
}));

/** partner_lead.category — DB 값(한글). 화면 표시도 이 값 그대로 */
export const LEAD_CATEGORIES = ["방송사", "제작사", "OTT", "유튜브 채널", "기타"];

/**
 * 담당자 이름·연락처·이메일·메신저 ID 패턴 — 서버 트리거(partner_lead_has_contact)와 같은 뜻. 서버가 최종.
 * @returns null | 'email' | 'phone' | 'messenger'
 */
export function containsContactInfo(text) {
  const s = String(text || "");
  if (!s) return null;
  if (/@/.test(s)) return "email";
  if (/(^|\D)\d{2,3}[-. ]?\d{3,4}[-. ]?\d{4}($|\D)/.test(s)) return "phone";
  if (/(카톡|카카오톡|카카오|kakao|라인|line|텔레그램|telegram)\s*(id|아이디|:|：)/i.test(s)) return "messenger";
  return null;
}
